import React, { useState, useEffect, useMemo } from 'react';
import { toast } from 'react-hot-toast';
import { buildEffectiveLeadsCsv } from '../utils/v3LeadsHelpers';

// ZeroBounce status options for filtering
const ZB_STATUS_OPTIONS = [
  { value: 'all', label: 'All Statuses', description: 'Show all records regardless of status' },
  { value: 'valid', label: 'Valid', description: 'Deliverable with <2% bounce rate. Safe to email.' },
  { value: 'invalid', label: 'Invalid', description: 'Undeliverable - mailbox doesn\'t exist or unreachable.' },
  { value: 'catch-all', label: 'Catch-all', description: 'Server accepts all emails. Deliverability uncertain.' },
  { value: 'unknown', label: 'Unknown', description: 'Could not complete validation. May be temporary.' },
  { value: 'spamtrap', label: 'Spamtrap', description: 'Suspected honeypot. Can damage sender reputation.' },
  { value: 'abuse', label: 'Abuse', description: 'Recipient has history of marking as spam.' },
  { value: 'do_not_mail', label: 'Do Not Mail', description: 'Risky - role-based, disposable, or suppression list.' }
];

// Get badge color based on ZeroBounce status
const getStatusBadgeClass = (status) => {
  if (!status) return 'bg-gray-100 text-gray-800';
  switch (status.toLowerCase()) {
    case 'valid':
      return 'bg-green-100 text-green-800';
    case 'invalid':
      return 'bg-red-100 text-red-800';
    case 'catch-all':
    case 'catch_all':
      return 'bg-yellow-100 text-yellow-800';
    case 'unknown':
      return 'bg-gray-100 text-gray-600';
    case 'spamtrap':
    case 'abuse':
      return 'bg-red-200 text-red-900';
    case 'do_not_mail':
      return 'bg-orange-100 text-orange-800';
    default:
      return 'bg-gray-100 text-gray-800';
  }
};

// Helper to build email_verification_id
const buildEmailVerificationId = (uniqueId, slot) => `${uniqueId}-e${slot}`;

const deriveExportNameParts = (record = {}) => {
  let firstName = record.first_name || record.first_name_1 || '';
  let lastName = record.last_name || record.last_name_1 || '';

  if ((!firstName || !lastName) && record.full_name) {
    const nameParts = record.full_name.trim().split(/\s+/);
    firstName = firstName || nameParts[0] || '';
    lastName = lastName || nameParts.slice(1).join(' ') || '';
  }

  return { firstName, lastName };
};

const formatListForCsv = (value) => {
  if (!Array.isArray(value)) return '';
  return value.join(';');
};

const EmailsTable = ({ enrichedRecords = [], nonEnrichedRecords = [], leadRows = null, loadState = null }) => {
  // State for pagination
  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(50);

  // State for ZeroBounce status filtering (replaces scoreThreshold)
  const [zbStatusFilter, setZbStatusFilter] = useState('all');
  
  // State for selection
  const [selectedEmails, setSelectedEmails] = useState(new Set());

  // State for duplicate removal
  const [removeDuplicates, setRemoveDuplicates] = useState(true);

  // Whether we're rendering Phase 6 provider-neutral effective rows (already
  // one-email-per-row) vs. the legacy email_1..4 flattening path.
  const usingLeadRows = Array.isArray(leadRows);
  const selectionReady = !usingLeadRows || !loadState || loadState.status === 'complete';

  useEffect(() => {
    if (usingLeadRows) {
      setSelectedEmails(new Set());
      setCurrentPage(1);
    }
  }, [usingLeadRows, leadRows]);

  // Process records to extract all emails as separate rows with ZeroBounce data.
  // When leadRows is provided, it is ALREADY one-email-per-row (Phase 6) — never
  // re-flatten it, never split/derive owner ids from unique_id strings.
  const emailRows = useMemo(() => {
    if (usingLeadRows) {
      return leadRows.map((row) => ({
        id: row.lead_id,
        email_verification_id: row.lead_id,
        unique_id: row.owner_unique_id,
        cad_owner_name: row.cad_owner_name || '',
        full_name: row.full_name || `${row.first_name || ''} ${row.last_name || ''}`,
        email: row.email,
        acres: row.acres,
        street: row.legal_address,
        zb_status: row.zerobounce_status || 'pending',
        zb_sub_status: row.zerobounce_sub_status || '',
        email_slot: null,
        // Phase 6-only fields (never present in the legacy path):
        enrichment_source: row.enrichment_source,
        contact_role: row.contact_role,
        name_match_score: row.name_match_score,
        final_match_status: row.final_match_status,
        _leadRow: row, // original row, used directly for CSV export — no re-lookup by id
      }));
    }

    const rows = [];
    
    enrichedRecords.forEach(record => {
      // Process email_1 through email_4
      for (let i = 1; i <= 4; i++) {
        const email = record[`email_${i}`];
        if (email) {  // Only add non-empty emails
          rows.push({
            id: `${record.unique_id}-email${i}`,
            email_verification_id: record[`email_verification_id_${i}`] || buildEmailVerificationId(record.unique_id, i),
            unique_id: record.unique_id,
            cad_owner_name: record.cad_owner_name || '',
            full_name: record.full_name || `${record.first_name || ''} ${record.last_name || ''}`,
            email: email,
            acres: record.acres,
            street: record.street,
            // ZeroBounce-only fields from unique_owner_email_zerobounce hydration
            zb_status: record[`zb_status_${i}`] || 'pending',
            zb_sub_status: record[`zb_sub_status_${i}`] || '',
            email_slot: i
          });
        }
      }
    });
    
    return rows;
  }, [enrichedRecords, usingLeadRows, leadRows]);
  
  // Filter rows based on ZeroBounce status
  const filteredRows = useMemo(() => {
    if (zbStatusFilter === 'all') {
      return emailRows;
    }
    return emailRows.filter(row => {
      const status = (row.zb_status || '').toLowerCase().replace('-', '_');
      const filterValue = zbStatusFilter.toLowerCase().replace('-', '_');
      return status === filterValue;
    });
  }, [emailRows, zbStatusFilter]);

  // Deduplicate rows based on email address
  const { displayRows, duplicatesRemoved } = useMemo(() => {
    if (!removeDuplicates) {
      return { displayRows: filteredRows, duplicatesRemoved: 0 };
    }
    
    const seen = new Set();
    const uniqueRows = [];
    
    for (const row of filteredRows) {
      const emailLower = row.email.toLowerCase();
      if (!seen.has(emailLower)) {
        seen.add(emailLower);
        uniqueRows.push(row);
      }
    }
    
    return { 
      displayRows: uniqueRows, 
      duplicatesRemoved: filteredRows.length - uniqueRows.length 
    };
  }, [filteredRows, removeDuplicates]);
  const selectedEligibleCount = displayRows.filter((row) => selectedEmails.has(row.id)).length;
  const selectAll = displayRows.length > 0 && selectedEligibleCount === displayRows.length;
  
  // Calculate pagination
  const totalPages = Math.ceil(displayRows.length / rowsPerPage);
  const paginatedRows = useMemo(() => {
    const startIdx = (currentPage - 1) * rowsPerPage;
    return displayRows.slice(startIdx, startIdx + rowsPerPage);
  }, [displayRows, currentPage, rowsPerPage]);
  
  // Reset to first page when filter changes
  useEffect(() => {
    setCurrentPage(1);
  }, [zbStatusFilter, removeDuplicates]);
  
  // Handle selection
  const toggleSelectEmail = (id) => {
    if (!selectionReady) return;
    const newSelected = new Set(selectedEmails);
    if (newSelected.has(id)) {
      newSelected.delete(id);
    } else {
      newSelected.add(id);
    }
    setSelectedEmails(newSelected);
    
  };
  
  const handleSelectAll = () => {
    if (!selectionReady) return;
    if (selectAll) {
      // Deselect all
      setSelectedEmails(new Set());
    } else {
      // Select all emails across all pages
      const allIds = new Set(displayRows.map(row => row.id));
      setSelectedEmails(allIds);
    }
  };

  const exportSelectedEmails = () => {
    if (!selectionReady) return;
    if (selectedEmails.size === 0) {
      toast.error('No emails selected');
      return;
    }
    
    const selectedRows = displayRows.filter(row => selectedEmails.has(row.id));

    // Phase 6 normalized rows: export directly from the original effective
    // rows (already carry county/address/list fields verbatim) — never look
    // up "original records" by owner-id splitting for this path.
    if (usingLeadRows) {
      const csvContent = buildEffectiveLeadsCsv(selectedRows.map((r) => r._leadRow));
      const countyNames = [...new Set(selectedRows.map((r) => r._leadRow.county_name).filter(Boolean))];
      const countyPart = countyNames.length === 1 ? countyNames[0]
        : countyNames.length > 1 && countyNames.length <= 3 ? countyNames.join('_')
          : countyNames.length > 3 ? `${countyNames.length}_counties` : 'unknown_county';
      const fileName = `${countyPart}_${selectedRows.length}_effective_leads.csv`;
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', fileName);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
      toast.success(`Exported ${selectedRows.length} leads to CSV`);
      return;
    }
    
    // Generate CSV content with ZeroBounce data
    const exportToCsv = (rows) => {
      // Define columns for export - includes email_verification_id and zb_status
      const headers = [
        'email_verification_id', 'unique_id', 'cad_owner_name', 'full_name_bd', 'first_name', 'last_name', 
        'state', 'email', 'zb_status', 'zb_sub_status', 'county_name', 'legal_address', 
        'mailing_address', 'street', 'city', 'zip', 'acres', 'mobile_1', 'mobile_2', 
        'mobile_3', 'mobile_4', 'land_id', 'owned_lands', 'acres_list', 'id_list',
        'market_value_list', 'owner_tax_year_list', 'deed_date_list'
      ];
      
      // Find original records to get additional data
      const selectedEmailsWithDetails = rows.map(row => {
        // Find the original enriched record
        const originalRecord = enrichedRecords.find(record => record.unique_id === row.unique_id);
        
        if (!originalRecord) return row;
        
        // Split the full name to get first and last name if not provided
        const { firstName, lastName } = deriveExportNameParts(originalRecord);
        
        return {
          email_verification_id: row.email_verification_id,
          unique_id: row.unique_id,
          email: row.email,
          zb_status: row.zb_status,
          zb_sub_status: row.zb_sub_status || '',
          cad_owner_name: originalRecord.cad_owner_name || '',
          full_name_bd: originalRecord.full_name || row.full_name,
          first_name: firstName,
          last_name: lastName,
          state: originalRecord.state || originalRecord.state_abbr || '',
          county_name: originalRecord.county_name || '',
          legal_address: originalRecord.legal_address || '',
          mailing_address: (() => {
            // Look up mailing_address from non-enriched records by unique_id
            const baseId = row.unique_id.split('-').slice(0, -1).join('-');
            const nonEnrichedRecord = nonEnrichedRecords.find(r => r.unique_id === row.unique_id || r.unique_id === baseId);
            return nonEnrichedRecord?.mailing_address || originalRecord.mailing_address || '';
          })(),
          street: originalRecord.street || row.street || '',
          acres: originalRecord.acres || row.acres || '',
          city: originalRecord.city || '',
          zip: originalRecord.zip || '',
          mobile_1: originalRecord.mobile_1 || '',
          mobile_2: originalRecord.mobile_2 || '',
          mobile_3: originalRecord.mobile_3 || '',
          mobile_4: originalRecord.mobile_4 || '',
          land_id: originalRecord.land_id || '',
          owned_lands: originalRecord.owned_lands || '',
          acres_list: originalRecord.acres_list || [],
          id_list: originalRecord.id_list || [],
          market_value_list: originalRecord.market_value_list || [],
          owner_tax_year_list: originalRecord.owner_tax_year_list || [],
          deed_date_list: originalRecord.deed_date_list || []
        };
      });
      
      // Format CSV content
      return [
        headers.join(','),
        ...selectedEmailsWithDetails.map(row => [
          row.email_verification_id || '',
          row.unique_id || '',
          `"${(row.cad_owner_name || '').replace(/"/g, '""')}"`,
          `"${(row.full_name_bd || '').replace(/"/g, '""')}"`,
          `"${(row.first_name || '').replace(/"/g, '""')}"`,
          `"${(row.last_name || '').replace(/"/g, '""')}"`,
          `"${(row.state || '').replace(/"/g, '""')}"`,
          `"${(row.email || '').replace(/"/g, '""')}"`,
          `"${(row.zb_status || '').replace(/"/g, '""')}"`,
          `"${(row.zb_sub_status || '').replace(/"/g, '""')}"`,
          `"${(row.county_name || '').replace(/"/g, '""')}"`,
          `"${(row.legal_address || '').replace(/"/g, '""')}"`,
          `"${(row.mailing_address || '').replace(/"/g, '""')}"`,
          `"${(row.street || '').replace(/"/g, '""')}"`,
          `"${(row.city || '').replace(/"/g, '""')}"`,
          `"${(row.zip || '').replace(/"/g, '""')}"`,
          row.acres || '',
          row.mobile_1 || '',
          row.mobile_2 || '',
          row.mobile_3 || '',
          row.mobile_4 || '',
          row.land_id || '',
          row.owned_lands || '',
          `"${formatListForCsv(row.acres_list)}"`,
          `"${formatListForCsv(row.id_list)}"`,
          `"${formatListForCsv(row.market_value_list)}"`,
          `"${formatListForCsv(row.owner_tax_year_list)}"`,
          `"${formatListForCsv(row.deed_date_list)}"`,
        ].join(','))
      ].join('\n');
    };
    
    // Get unique county names from selected rows
    const countyNames = [...new Set(
      selectedRows
        .map(row => {
          const originalRecord = enrichedRecords.find(r => r.unique_id === row.unique_id);
          return originalRecord?.county_name;
        })
        .filter(Boolean)
    )];
    
    // Build filename with all county names (or count if too many)
    let countyPart;
    if (countyNames.length === 0) {
      countyPart = 'unknown_county';
    } else if (countyNames.length === 1) {
      countyPart = countyNames[0];
    } else if (countyNames.length <= 3) {
      countyPart = countyNames.join('_');
    } else {
      countyPart = `${countyNames.length}_counties`;
    }
    
    // Create filename: county(s)_count_enriched_zerobounce_verified.csv
    const fileName = `${countyPart}_${selectedRows.length}_enriched_zerobounce_verified.csv`;
    
    // Download the CSV
    const csvContent = exportToCsv(selectedRows);
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', fileName);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
    
    toast.success(`Exported ${selectedRows.length} emails to CSV`);
  };

  // Count pending (not yet verified) records
  const pendingCount = useMemo(() => 
    emailRows.filter(row => !row.zb_status || row.zb_status === 'pending').length,
    [emailRows]
  );

  return (
    <div className="bg-white rounded-lg shadow-sm p-4 mt-6 w-full">
      <div className="flex justify-between items-center mb-4">
        <h2 className="text-lg font-semibold">Email Records</h2>
        <div className="text-sm">
          {usingLeadRows ? 'Effective rows loaded: ' : 'Total Emails: '}<span className="font-semibold">{loadState?.loaded ?? emailRows.length}</span>
          {usingLeadRows && <> / {loadState?.total ?? emailRows.length}</>} |{' '}
          Filtered: <span className="font-semibold">{filteredRows.length}</span> |{' '}
          Display eligible: <span className="font-semibold">{displayRows.length}</span> |{' '}
          Selected: <span className="font-semibold">{selectedEligibleCount}</span>
          {removeDuplicates && (
            <span className="text-orange-600 ml-1">
              ({duplicatesRemoved} duplicates removed)
            </span>
          )}
        </div>
      </div>

      {/* ZeroBounce verification status banner */}
      {pendingCount > 0 && (
        <div className="mb-4 p-3 bg-amber-50 border border-amber-200 rounded-lg flex items-start gap-2">
          <span className="text-amber-500 mt-0.5">⚠️</span>
          <div>
            <p className="text-sm font-medium text-amber-800">
              {pendingCount} of {emailRows.length} emails have not been ZeroBounce verified yet
            </p>
            <p className="text-xs text-amber-600 mt-1">
              Enable ZeroBounce verification and click "Enrich & Verify" to verify these email slots, or check that your ZeroBounce API key is configured on the server.
            </p>
          </div>
        </div>
      )}
      {pendingCount === 0 && emailRows.length > 0 && (
        <div className="mb-4 p-3 bg-green-50 border border-green-200 rounded-lg flex items-start gap-2">
          <span className="text-green-500 mt-0.5">✅</span>
          <p className="text-sm font-medium text-green-800">
            All {emailRows.length} emails have been ZeroBounce verified
          </p>
        </div>
      )}
      
      <div className="flex flex-wrap gap-4 mb-4">
        <div className="flex items-center">
          <label htmlFor="zbStatusFilter" className="mr-2 text-sm font-medium">
            ZeroBounce Status:
          </label>
          <select
            id="zbStatusFilter"
            value={zbStatusFilter}
            onChange={(e) => setZbStatusFilter(e.target.value)}
            className="border rounded px-3 py-1.5 text-sm min-w-[160px]"
          >
            {ZB_STATUS_OPTIONS.map(option => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
          {zbStatusFilter !== 'all' && (
            <span className="ml-2 text-xs text-gray-500 max-w-xs truncate" title={ZB_STATUS_OPTIONS.find(o => o.value === zbStatusFilter)?.description}>
              {ZB_STATUS_OPTIONS.find(o => o.value === zbStatusFilter)?.description}
            </span>
          )}
        </div>
        
        <div className="flex items-center">
          <button
            onClick={handleSelectAll}
            disabled={!selectionReady}
            className="px-3 py-1 bg-blue-100 text-blue-700 rounded hover:bg-blue-200"
          >
            {selectAll ? 'Deselect All' : 'Select All'}
          </button>

          <button
            onClick={exportSelectedEmails}
            disabled={!selectionReady || selectedEligibleCount === 0}
            className={`ml-2 px-3 py-1 rounded ${
              selectionReady && selectedEligibleCount > 0
                ? 'bg-green-600 text-white hover:bg-green-700' 
                : 'bg-gray-300 text-gray-500'
            }`}
          >
            Export Selected to CSV
          </button>

          <label className="ml-4 flex items-center cursor-pointer">
          <input
            type="checkbox"
            checked={removeDuplicates}
            onChange={(e) => setRemoveDuplicates(e.target.checked)}
            className="mr-2"
          />
          <span className="text-sm">Remove duplicate emails</span>
          </label>
        </div>
        
        <div className="flex items-center ml-auto">
          <label htmlFor="rowsPerPage" className="mr-2 text-sm">
            Rows per page:
          </label>
          <select
            id="rowsPerPage"
            value={rowsPerPage}
            onChange={(e) => setRowsPerPage(parseInt(e.target.value))}
            className="border rounded px-2 py-1"
          >
            <option value={25}>25</option>
            <option value={50}>50</option>
            <option value={100}>100</option>
          </select>
        </div>
      </div>
      
      {displayRows.length > 0 ? (
        <>
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-left">
                    <input 
                      type="checkbox" 
                      checked={selectAll}
                      disabled={!selectionReady}
                      onChange={handleSelectAll}
                    />
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Email Verification ID
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Unique ID
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Full Name
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Email
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    ZB Status
                  </th>
                  {usingLeadRows && (
                    <>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Source
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Contact Role
                      </th>
                    </>
                  )}
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {paginatedRows.map(row => (
                  <tr key={row.id} className="hover:bg-gray-50">
                    <td className="px-4 py-4">
                      <input 
                        type="checkbox" 
                        checked={selectedEmails.has(row.id)} 
                        disabled={!selectionReady}
                        onChange={() => toggleSelectEmail(row.id)}
                      />
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 font-mono">
                      {row.email_verification_id}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {row.unique_id}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {row.full_name}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-blue-600">
                      {row.email}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm">
                      <span className={`px-2 py-1 rounded text-xs ${getStatusBadgeClass(row.zb_status)}`}>
                        {row.zb_status || 'pending'}
                      </span>
                    </td>
                    {usingLeadRows && (
                      <>
                        <td className="px-6 py-4 whitespace-nowrap text-xs">
                          <span className="px-2 py-1 rounded bg-indigo-100 text-indigo-800">{row.enrichment_source || '—'}</span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-xs text-gray-600">
                          {row.contact_role || '—'}
                        </td>
                      </>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          
          {/* Pagination controls */}
          <div className="mt-4 flex items-center justify-between">
          <div className="text-sm text-gray-500">
            Showing {Math.min(displayRows.length, (currentPage - 1) * rowsPerPage + 1)} to {Math.min(displayRows.length, currentPage * rowsPerPage)} of {displayRows.length} emails
          </div>
            
            <div className="flex">
              <button
                onClick={() => setCurrentPage(1)}
                disabled={currentPage === 1}
                className="px-3 py-1 border rounded-l-md bg-white disabled:bg-gray-100 disabled:text-gray-400"
              >
                First
              </button>
              <button
                onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                disabled={currentPage === 1}
                className="px-3 py-1 border-t border-b bg-white disabled:bg-gray-100 disabled:text-gray-400"
              >
                Prev
              </button>
              <span className="px-3 py-1 border-t border-b bg-blue-50">
                {currentPage} / {totalPages || 1}
              </span>
              <button
                onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                disabled={currentPage >= totalPages}
                className="px-3 py-1 border-t border-b bg-white disabled:bg-gray-100 disabled:text-gray-400"
              >
                Next
              </button>
              <button
                onClick={() => setCurrentPage(totalPages)}
                disabled={currentPage >= totalPages}
                className="px-3 py-1 border rounded-r-md bg-white disabled:bg-gray-100 disabled:text-gray-400"
              >
                Last
              </button>
            </div>
          </div>
        </>
      ) : (
        <div className="py-8 text-center text-gray-500">
          No email records match the current criteria
        </div>
      )}
    </div>
  );
};

export default EmailsTable;
